import React, { forwardRef } from 'react';

/**
 * ResponsiveGalleryImage - A component that displays gallery images with responsive WebP optimization
 * 
 * @param {Object} props
 * @param {string} props.src - The original image source path
 * @param {string} props.alt - Alt text for the image
 * @param {string} props.className - CSS class name
 * @param {Object} props.style - Inline styles
 * @param {Function} props.onLoad - Load handler
 * @param {Function} props.onError - Error handler
 * @param {Function} props.onClick - Click handler
 * @param {string} props.type - Image type: 'headshot' or 'shot' (affects optimization settings)
 */
const ResponsiveGalleryImage = forwardRef(({ 
  src, 
  alt, 
  className = '', 
  style = {},
  onLoad,
  onError,
  onClick,
  type = 'shot' // 'headshot' or 'shot'
}, ref) => {
  // Extract avatar ID and filename from path
  // Expected format: /img/avatars/{avatarId}/{type}/{filename}
  const pathMatch = src.match(/\/img\/avatars\/([^\/]+)\/([^\/]+)\/(.+)/);
  
  if (!pathMatch) {
    // Fallback to original image if path doesn't match expected format
    return (
      <img
        ref={ref}
        src={src}
        alt={alt}
        className={className}
        style={style}
        onLoad={onLoad}
        onError={onError}
        onClick={onClick}
      />
    );
  }

  const [, avatarId, imageType, filename] = pathMatch;
  const baseName = filename.replace(/\.[^/.]+$/, ""); // Remove extension
  
  // Generate responsive image sources with standard breakpoints
  const generateSrcSet = (size) => {
    const sizeMap = {
      small: { width: 1024 },
      medium: { width: 1280 },
      large: { width: 1440 }
    };
    
    const sizeConfig = sizeMap[size];
    
    // Use pre-generated WebP images from cache directory
    // Convert path from /img/avatars/{avatarId}/{type}/{filename} to /img/cache/avatars/{avatarId}/{type}/{filename}-{size}-{width}w.webp
    const relativePath = src.replace(/^\/img\//, '');
    const pathParts = relativePath.split('/');
    const filename = pathParts[pathParts.length - 1];
    const filenameWithoutExt = filename.replace(/\.[^/.]+$/, '');
    
    // Build cache path: /img/cache/avatars/{avatarId}/{type}/{filename}-{size}-{width}w.webp
    const cachePath = `/img/cache/${pathParts.slice(0, -1).join('/')}/${filenameWithoutExt}-${size}-${sizeConfig.width}w.webp`;
    
    return `${cachePath} ${sizeConfig.width}w`;
  };

  // Generate sizes attribute for responsive loading
  const generateSizes = () => {
    return '(max-width: 768px) 1024px, (max-width: 1024px) 1280px, 1440px';
  };

  // Use the large WebP version for optimal quality
  const getWebPImageSrc = () => {
    // Remove leading slash to get the relative path
    const relativePath = src.replace(/^\//, '');
    const pathParts = relativePath.split('/');
    const filename = pathParts[pathParts.length - 1];
    const filenameWithoutExt = filename.replace(/\.[^/.]+$/, '');
    
    // Use the large WebP version (1440px)
    // Skip the first part (img) and last part (filename) since we're going to /img/cache/
    const directoryPath = pathParts.slice(1, -1).join('/');
    const cachePath = `/img/cache/${directoryPath}/${filenameWithoutExt}-large-1440w.webp`;
    
    
    return cachePath;
  };

  return (
    <img
      ref={ref}
      src={getWebPImageSrc()}
      alt={alt}
      className={className}
      style={style}
      onLoad={(e) => {
        // console.log('WebP image loaded successfully:', e.target.src);
        if (onLoad) onLoad(e);
      }}
      onError={(e) => {
        // Only fallback if we're currently trying to load a WebP image
        if (e.target.src.includes('.webp')) {
          // Convert WebP path back to original PNG path for fallback
          const originalPngPath = src.replace(/\/img\/cache\//, '/img/').replace(/-large-1440w\.webp$/, '.png');
          e.target.src = originalPngPath;
        }
        if (onError) onError(e);
      }}
      onClick={onClick}
      loading={type === 'headshot' ? 'eager' : 'lazy'}
      decoding="async"
    />
  );
});

export default ResponsiveGalleryImage;
